import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { deleteTodo, fetchTodos } from "../api/selectTodo";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";

import { useConfig } from "../contexts/ConfigContext";
function getDatesInMonth(year, month) {
  const dates = [];
  const date = new Date(year, month - 1, 1); // month is 1-indexed
  while (date.getMonth() === month - 1) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return dates;
}

function getTodoButtonSx(theme, { todo, isDeleteMode, isSelected, compact = false }) {
  const completed = Boolean(todo.is_completed);
  const completedBase = isSelected
    ? alpha(theme.palette.primary.main, 0.14)
    : alpha(theme.palette.primary.dark, 0.05);
  const completedHover = isSelected
    ? alpha(theme.palette.primary.main, 0.18)
    : alpha(theme.palette.primary.dark, 0.08);
  const defaultBase = isSelected
    ? alpha(theme.palette.primary.main, 0.16)
    : alpha(theme.palette.primary.dark, 0.05);
  const defaultHover = isSelected
    ? alpha(theme.palette.primary.main, 0.22)
    : alpha(theme.palette.primary.dark, 0.1);

  return {
    justifyContent: "flex-start",
    fontSize: compact ? "0.9rem" : "14px",
    lineHeight: 1.1,
    padding: compact
      ? (isDeleteMode ? "6px 10px 6px 8px" : "6px 12px")
      : (isDeleteMode ? "1px 6px 1px 2px" : "1px 8px"),
    minHeight: compact ? "36px" : "24px",
    borderRadius: compact ? 1.5 : "10px",
    color: completed ? theme.palette.text.secondary : theme.palette.text.primary,
    backgroundColor: completed ? completedBase : defaultBase,
    borderColor: completed
      ? alpha(theme.palette.primary.dark, 0.16)
      : alpha(theme.palette.primary.dark, 0.12),
    "&:hover": {
      backgroundColor: completed ? completedHover : defaultHover,
      borderColor: completed
        ? alpha(theme.palette.primary.dark, 0.22)
        : alpha(theme.palette.primary.dark, 0.18),
    },
  };
}

function TodoLabel({ todo, compact = false }) {
  const completed = Boolean(todo.is_completed);
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: compact ? 0.5 : 0.25 }}
    >
      {completed && (
        <CheckCircleRoundedIcon
          fontSize="inherit"
          color="primary"
          sx={{ fontSize: compact ? 18 : 16, flexShrink: 0 }}
        />
      )}
      <Box component="span">{todo.name}</Box>
    </Box>
  );
}

function BasicTable({
  rows,
  isDeleteMode,
  selectedTodosForDelete,
  onSelectTodoForDelete,
  onNavigateToTodo,
  theme,
}) {
  const columns = ["日付", "ワースケ"];
  const borderStyle = "1px solid rgba(224, 224, 224, 1)";

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{
          minWidth: 720,
          "& .MuiTableCell-root": { fontSize: "0.95rem", py: 0.5, px: 1.5 },
        }}
        aria-label="basic table"
      >
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column}
                style={{
                  borderRight: index === 0 ? borderStyle : undefined,
                  width: index === 0 ? 146 : undefined,
                  whiteSpace: index === 0 ? "nowrap" : undefined,
                }}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(rows).map(([dateString, todos]) => {
            const date = new Date(dateString);
            const formattedDate = format(date, "M月d日（E）", { locale: ja });
            const weekday = format(date, "eeee", { locale: ja });
            let textColor;
            if (weekday === "土曜日") {
              textColor = "rgba(0, 0, 255, 0.6)"; // 薄い青色
            } else if (weekday === "日曜日") {
              textColor = "rgba(255, 0, 0, 0.6)"; // 薄い赤色
            }
            return (
              <TableRow key={formattedDate}>
                <TableCell
                  style={{
                    borderRight: borderStyle,
                    color: textColor,
                    width: "146px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formattedDate}
                </TableCell>
                <TableCell>
                  {todos.map((todo) => (
                    <Box
                      key={todo.id}
                      sx={{ display: "inline-block", mr: 0.75, mb: 0 }}
                    >
                      <Button
                        variant="outlined"
                        color="inherit"
                        sx={getTodoButtonSx(theme, {
                          todo,
                          isDeleteMode,
                          isSelected: selectedTodosForDelete.includes(todo.public_id),
                        })}
                        onClick={
                          isDeleteMode
                            ? (e) => {
                                e.preventDefault();
                                onSelectTodoForDelete(todo.public_id);
                              }
                            : () => onNavigateToTodo(todo.public_id)
                        }
                      >
                        {isDeleteMode && (
                          <Checkbox
                            checked={selectedTodosForDelete.includes(todo.public_id)}
                            onChange={() => onSelectTodoForDelete(todo.public_id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: 0, marginRight: "8px" }}
                          />
                        )}
                        <TodoLabel todo={todo} />
                      </Button>
                    </Box>
                  ))}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function MobileTodoList({
  rows,
  isDeleteMode,
  selectedTodosForDelete,
  onSelectTodoForDelete,
  onNavigateToTodo,
  theme,
}) {
  return (
    <Stack spacing={1}>
      {Object.entries(rows).map(([dateString, todos]) => {
        const date = new Date(dateString);
        const formattedDate = format(date, "M月d日（E）", { locale: ja });

        return (
          <Card key={dateString}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1">{formattedDate}</Typography>
                {todos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    ワースケはありません。
                  </Typography>
                ) : (
                  todos.map((todo) => (
                    <Button
                      key={todo.id}
                      variant="outlined"
                      color="inherit"
                      onClick={() =>
                        isDeleteMode
                          ? onSelectTodoForDelete(todo.public_id)
                          : onNavigateToTodo(todo.public_id)
                      }
                      startIcon={
                        isDeleteMode ? (
                          <Checkbox
                            checked={selectedTodosForDelete.includes(todo.public_id)}
                            onChange={() => onSelectTodoForDelete(todo.public_id)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0 }}
                          />
                        ) : null
                      }
                      sx={getTodoButtonSx(theme, {
                        todo,
                        isDeleteMode,
                        isSelected: selectedTodosForDelete.includes(todo.public_id),
                        compact: true,
                      })}
                    >
                      <TodoLabel todo={todo} compact />
                    </Button>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

function convertTodos(todos, year, month) {
  const date2Todo = {};
  const dates = getDatesInMonth(year, month);

  dates.forEach((date) => {
    date2Todo[date] = [];
  });

  todos.forEach((item) => {
    const scheduled = item.scheduled_on;
    const [sy, sm, sd] = String(scheduled).split("-").map(Number);
    const itemDate = new Date(sy, sm - 1, sd);
    const keyDate = dates.find(
      (date) =>
        date.getDate() === itemDate.getDate() &&
        date.getMonth() === itemDate.getMonth()
    );
    if (keyDate) {
      date2Todo[keyDate].push(item);
    }
  });

  return date2Todo;
}

function getNewYearMonthString(currentYear, currentMonth, offset) {
  let newMonth = currentMonth + offset;
  let newYear = currentYear;

  if (newMonth > 12) {
    newMonth = 1;
    newYear += 1;
  } else if (newMonth < 1) {
    newMonth = 12;
    newYear -= 1;
  }

  const newYearString = String(newYear);
  const newMonthString = String(newMonth).padStart(2, "0");

  return { newYearString, newMonthString };
}

function MonthNavigationButton({ icon, onClick, ariaLabel }) {
  return (
    <IconButton
      onClick={onClick}
      aria-label={ariaLabel}
      color="primary"
    >
      {icon}
    </IconButton>
  );
}

function SelectTodo() {
  const params = useParams();
  const year = parseInt(params.year, 10);
  const month = parseInt(params.month, 10);

  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedTodosForDelete, setSelectedTodosForDelete] = useState([]);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showFeedback = (message, severity = "info") => {
    setFeedback({ open: true, message, severity });
  };

  useEffect(() => {
    setLoading(true);
    fetchTodos(year, month)
      .then((response) => {
        setTodos(response);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching todo lists:", error);
        setLoading(false); // エラーが発生しても loading を false に設定する
      });
  }, [year, month]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("action") === "enterDeleteMode") {
      setIsDeleteMode(true);
      setSelectedTodosForDelete([]);
      navigate(location.pathname, { replace: true });
      showFeedback("削除モードで再開しました。", "info");
    }
  }, [location.search, navigate, location.pathname]);

  if (loading) {
    return <Loading />;
  }

  const rows = convertTodos(todos, year, month);

  const onNavigate = (offset) => {
    const { newYearString, newMonthString } = getNewYearMonthString(
      year,
      month,
      offset
    );
    navigate(`/todos/${newYearString}/${newMonthString}`);
  };

  const toggleDeleteMode = () => {
    const forceLogin = Boolean(config?.features?.forceLoginOnDeleteTodo);

    if (forceLogin) {
      showFeedback(
        "削除操作のために再ログインが必要です。ログイン後、削除モードで再開します。",
        "warning"
      );
      const redirectPath = `/todos/${year}/${month}`;
      navigate(
        `/login?redirect=${encodeURIComponent(
          redirectPath
        )}&action=enterDeleteMode`
      );
      return;
    }

    setIsDeleteMode(!isDeleteMode);
    setSelectedTodosForDelete([]);
  };

  const handleSelectTodoForDelete = (todoId) => {
    setSelectedTodosForDelete((prev) =>
      prev.includes(todoId)
        ? prev.filter((id) => id !== todoId)
        : [...prev, todoId]
    );
  };

  const handleDeleteSelectedTodosForDelete = async () => {
    setLoading(true);
    try {
      for (const todoId of selectedTodosForDelete) {
        await deleteTodo(todoId);
      }
      const response = await fetchTodos(year, month);
      setTodos(response);
      showFeedback("選択されたワースケを削除しました。", "success");
    } catch (error) {
      console.error("Todoの削除中にエラーが発生しました:", error);
      showFeedback("ワースケの削除中にエラーが発生しました。", "error");
    } finally {
      setLoading(false);
      setIsDeleteMode(false);
      setSelectedTodosForDelete([]);
    }
  };

  const handleNavigateToTodo = (todoPublicId) => {
    navigate(`/todos/${todoPublicId}`);
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <MonthNavigationButton
              icon={<ChevronLeftRoundedIcon />}
              onClick={() => onNavigate(-1)}
              ariaLabel="前月へ移動"
            />
            <Typography variant="h5" sx={{ whiteSpace: "nowrap" }}>
              {params.year}年{params.month}月
            </Typography>
            <MonthNavigationButton
              icon={<ChevronRightRoundedIcon />}
              onClick={() => onNavigate(1)}
              ariaLabel="翌月へ移動"
            />
          </Stack>
          {isDeleteMode ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="削除モード" color="error" variant="filled" />
              <Button variant="outlined" size="small" onClick={toggleDeleteMode}>
                キャンセル
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleDeleteSelectedTodosForDelete}
                disabled={selectedTodosForDelete.length === 0}
                startIcon={<DeleteOutlineRoundedIcon />}
              >
                選択項目を削除
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="閲覧モード" color="primary" variant="outlined" />
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={toggleDeleteMode}
                startIcon={<DeleteOutlineRoundedIcon />}
              >
                削除モードに変更する
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {!todos.length ? (
        <Alert severity="info">
          この月にはワースケがありません。テンプレートから新規作成してください。
        </Alert>
      ) : isMobile ? (
        <MobileTodoList
          rows={rows}
          isDeleteMode={isDeleteMode}
          selectedTodosForDelete={selectedTodosForDelete}
          onSelectTodoForDelete={handleSelectTodoForDelete}
          onNavigateToTodo={handleNavigateToTodo}
          theme={theme}
        />
      ) : (
        <BasicTable
          rows={rows}
          isDeleteMode={isDeleteMode}
          selectedTodosForDelete={selectedTodosForDelete}
          onSelectTodoForDelete={handleSelectTodoForDelete}
          onNavigateToTodo={handleNavigateToTodo}
          theme={theme}
        />
      )}

      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </Stack>
  );
}

export default SelectTodo;
